import { api } from './api';

export interface DocumentNodePermission {
  id: string;
  documentId: string;
  sourceType: string;
  sourceId: string;
  permissionId: string;
  action: string;
  createdAt: string;
  createdBy?: string;
}

export interface DocumentNodePermissionsDto {
  documentId: string;
  inheritPermissions: boolean;
  permissionGroups: PermissionGroupDto[];
}

export interface PermissionGroupItemDto {
  permissionId: string;
  action: string;
}

export interface PermissionGroupDto {
  id: string;
  type: string;
  name: string;
  permissions: PermissionGroupItemDto[];
}

export interface AvailableRolesDto {
  id: string;
  name: string;
}

export interface AvailableTeamsDto {
  id: string;
  name: string;
}

export interface AvailableUsersDto {
  id: string;
  name: string;
  email: string;
}

class DocumentNodePermissionsService {
  async getDocumentNodePermissions(documentId: string): Promise<DocumentNodePermissionsDto> {
    try {
      const response = await api.get(`/documents/nodes/permissions/${documentId}`);
      return response.data;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.getDocumentNodePermissions] Erro na requisição:', error);
      throw error;
    }
  }

  async getDocumentNodePermissionsBySource(documentId: string, sourceType: string, sourceId: string): Promise<string[]> {
    try {
      const response = await api.get(`/documents/nodes/permissions/${documentId}/source/${sourceType}/${sourceId}`);
      return response.data;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.getDocumentNodePermissionsBySource] Erro na requisição:', error);
      throw error;
    }
  }

  async updateDocumentNodePermissions(documentId: string, permissions: DocumentNodePermissionsDto): Promise<string> {
    try {
      const payload = {
        documentId: documentId,
        inheritPermissions: permissions.inheritPermissions,
        permissionGroups: permissions.permissionGroups || []
      };
      
      const response = await api.put(`/documents/nodes/permissions`, payload);
      
      // Determinar mensagem baseada nos dados enviados
      let message = '';
      if (permissions.inheritPermissions) {
        message = 'Permissões atualizadas com sucesso! O documento agora herda permissões do documento pai.';
      } else if (permissions.permissionGroups && permissions.permissionGroups.length > 0) {
        const totalPermissions = permissions.permissionGroups.reduce((total, group) => 
          total + (group.permissions?.length || 0), 0);
        message = `Permissões atualizadas com sucesso! ${totalPermissions} permissão(ões) foram configurada(s) para o documento.`;
      } else {
        message = 'Permissões atualizadas com sucesso! Nenhuma permissão específica foi configurada.';
      }
      
      return message;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.updateDocumentNodePermissions] Erro na requisição:', error);
      throw error;
    }
  }

  async updateDocumentNodePermissionsBatch(documentId: string, sourceType: string, sourceId: string, permissionIds: string[], action: string = 'allow'): Promise<string> {
    try {
      const response = await api.put(`/documents/nodes/permissions/${documentId}/source/${sourceType}/${sourceId}`, { 
        permissionIds,
        action 
      });
      
      const actionText = action === 'allow' ? 'permitidas' : 'negadas';
      return `Permissões ${actionText} com sucesso para ${permissionIds.length} permissão(ões).`;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.updateDocumentNodePermissionsBatch] Erro na requisição:', error);
      throw error;
    }
  }

  async addDocumentNodePermission(documentId: string, sourceType: string, sourceId: string, permissionId: string, action: string = 'allow'): Promise<string> {
    try {
      const response = await api.post(`/documents/nodes/permissions/${documentId}/source/${sourceType}/${sourceId}/permissions/${permissionId}?action=${action}`);
      
      const actionText = action === 'allow' ? 'permitida' : 'negada';
      return `Permissão ${actionText} com sucesso para o documento.`;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.addDocumentNodePermission] Erro na requisição:', error);
      throw error;
    }
  }

  async removeDocumentNodePermission(documentId: string, sourceType: string, sourceId: string, permissionId: string): Promise<string> {
    try {
      const response = await api.delete(`/documents/nodes/permissions/${documentId}/source/${sourceType}/${sourceId}/permissions/${permissionId}`);
      return 'Permissão removida com sucesso do documento.';
    } catch (error: any) {
      console.error('[documentNodePermissionsService.removeDocumentNodePermission] Erro na requisição:', error);
      throw error;
    }
  }

  async getAvailableRoles(): Promise<AvailableRolesDto[]> {
    try {
      const response = await api.get('/documents/nodes/permissions/roles');
      return response.data;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.getAvailableRoles] Erro na requisição:', error);
      throw error;
    }
  }

  async getAvailableTeams(): Promise<AvailableTeamsDto[]> {
    try {
      const response = await api.get('/documents/nodes/permissions/teams');
      return response.data;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.getAvailableTeams] Erro na requisição:', error);
      throw error;
    }
  }

  async getAvailableUsers(): Promise<AvailableUsersDto[]> {
    try {
      const response = await api.get('/documents/nodes/permissions/users');
      return response.data;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.getAvailableUsers] Erro na requisição:', error);
      throw error;
    }
  }

  async verifyPermission(documentId: string, moduleName: string, actionName: string): Promise<boolean> {
    try {
      const response = await api.get('/documents/nodes/permissions/verify', {
        params: {
          documentId,
          moduleName,
          actionName
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('[documentNodePermissionsService.verifyPermission] Erro na requisição:', error);
      // Em caso de erro, retorna false para não permitir a ação
      return false;
    }
  }
}

export const documentNodePermissionsService = new DocumentNodePermissionsService(); 